import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { reviewAPI, emailAPI } from '@/services/api';
import { NotificationCenter } from './NotificationCenter';
import {
  Inbox,
  BarChart3,
  MessageSquare,
  Settings,
  Search,
  HelpCircle,
  Plus,
  ChevronDown,
  AtSign,
  UserX,
  User,
  ArrowUpDown,
  Tag,
  PanelLeftClose,
  MoreVertical,
  Loader2,
  Truck,
  AlertTriangle,
  RotateCcw,
  XCircle,
  ChevronLeft,
  ChevronRight,
  HelpCircle as HelpIcon
} from 'lucide-react';
import type { SortDir, SortField } from '@/services/api';

const SORT_LABELS: Record<SortField, string> = {
  received_at: 'Created',
  customer: 'Customer',
  subject: 'Latest update',
  category: 'Category',
  status: 'Status',
};

interface ListPagination {
  page: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

interface ListSort {
  field: SortField;
  dir: SortDir;
  onSort: (field: SortField) => void;
}

interface EDeskLayoutProps {
  children: React.ReactNode;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onSearchChange?: (search: string) => void;
  searchQuery?: string;
  selectedPlatform?: string;
  onPlatformChange?: (platform: string) => void;
  totalCount?: number;
  pagination?: ListPagination;
  sort?: ListSort;
}

export default function EDeskLayout({
  children,
  activeTab = 'Pending Review',
  onTabChange,
  onSearchChange,
  searchQuery = '',
  selectedPlatform = 'all',
  onPlatformChange,
  totalCount = 0,
  pagination,
  sort,
}: EDeskLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentTab, setCurrentTab] = useState(activeTab);
  const [secondaryCollapsed, setSecondaryCollapsed] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const isMailboxPage = location.pathname === '/' || location.pathname.startsWith('/emails') || location.pathname.startsWith('/review');

  const { data: stats } = useQuery({
    queryKey: ['reviewStats'],
    queryFn: async () => {
      const res = await reviewAPI.getStats();
      return res.data;
    },
    refetchInterval: 2000,
  });

  const { data: categoryStatsData } = useQuery({
    queryKey: ['categoryStats'],
    queryFn: async () => {
      const res = await reviewAPI.getCategoryStats();
      return res.data;
    },
    refetchInterval: 2000,
  });

  const catMap: Record<string, number> = {};
  if (categoryStatsData?.categories && Array.isArray(categoryStatsData.categories)) {
    categoryStatsData.categories.forEach((item: any) => {
      if (item.category) {
        catMap[item.category] = item.count;
      }
    });
  }

  const handleTabClick = (tabName: string) => {
    setCurrentTab(tabName);
    if (onTabChange) {
      onTabChange(tabName);
    }
  };

  const processEmailsMutation = useMutation({
    mutationFn: async () => {
      setIsProcessing(true);
      const res = await emailAPI.process({ max_emails: 10 });
      return res.data;
    },
    onSuccess: (data) => {
      setIsProcessing(false);
      toast.success(data?.message || 'Processed emails successfully!');
      queryClient.invalidateQueries({ queryKey: ['reviewQueue'] });
      queryClient.invalidateQueries({ queryKey: ['reviewStats'] });
      queryClient.invalidateQueries({ queryKey: ['categoryStats'] });
    },
    onError: (error: any) => {
      setIsProcessing(false);
      toast.error(error?.response?.data?.detail || 'Failed to process emails');
    },
  });

  const handleProcessEmails = () => {
    toast.loading('Fetching unread emails from Gmail...', { id: 'process-emails' });
    processEmailsMutation.mutate(undefined, {
      onSettled: () => {
        toast.dismiss('process-emails');
      },
    });
  };

  return (
    <div className="flex flex-col h-screen w-screen bg-white font-sans overflow-hidden select-none">
      <div className="bg-[#2563EB] text-white text-[13px] py-[6px] px-4 text-center font-normal flex items-center justify-center shrink-0 z-50">
        <span>
          EDesk Alternative · Automated Email Processing & AI Responses
        </span>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        <aside className="w-[54px] bg-[#181F38] flex flex-col items-center py-3 justify-between shrink-0 z-40 text-slate-300">
          <div className="flex flex-col items-center space-y-3 w-full">
            <Link
              to="/dashboard"
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-lg mb-1 hover:bg-slate-800 cursor-pointer"
              title="Dashboard"
            >
              <div className="w-7 h-7 rounded bg-blue-600 flex items-center justify-center font-bold text-xs text-white">
                eD
              </div>
            </Link>

            <button
              onClick={() => navigate('/emails')}
              title="Mailbox"
              className={`relative group cursor-pointer w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                location.pathname.startsWith('/emails') || location.pathname.startsWith('/review') || location.pathname === '/'
                  ? 'bg-[#2563EB] text-white shadow-sm'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Inbox className="w-5 h-5" />
              {(stats?.pending_review ?? 0) > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-[#181F38]" />
              )}
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              title="Analytics"
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                location.pathname === '/dashboard'
                  ? 'bg-[#2563EB] text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
            </button>

            <Link
              to="/settings"
              title="Settings"
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                location.pathname === '/settings'
                  ? 'bg-[#2563EB] text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              <Settings className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex flex-col items-center space-y-3 w-full">
            <div className="w-9 h-9 rounded-full bg-[#F59E0B] text-white flex items-center justify-center font-bold text-xs shadow-inner cursor-pointer hover:opacity-90">
              HR
            </div>
          </div>
        </aside>

        {isMailboxPage && (
          <aside
            className={`${
              secondaryCollapsed ? 'w-0 overflow-hidden opacity-0' : 'w-[220px] opacity-100'
            } bg-[#F8FAFC] border-r border-slate-200 flex flex-col justify-between transition-all duration-200 shrink-0 z-30`}
          >
            <div className="p-3 flex flex-col flex-1 overflow-y-auto">
              <div className="flex items-center justify-between mb-3 px-1">
                <h2 className="text-xl font-bold text-slate-900 tracking-tight">Mailbox</h2>
              </div>

              <div className="space-y-0.5 text-xs font-medium">
                <div className="pb-1 px-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Workflows
                </div>

                <button
                  onClick={() => handleTabClick('Pending Review')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Pending Review' || currentTab === 'Pre-sales' || currentTab === 'New'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <Tag className="w-4 h-4 text-blue-600" />
                    <span>Pending Review</span>
                  </span>
                  <span className="px-2 py-0.5 text-[11px] font-bold bg-blue-100 text-blue-800 rounded-full">
                    {stats?.pending_review ?? 0}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick('Approved')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Approved' || currentTab === 'My tickets' || currentTab === 'To do'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <User className="w-4 h-4 text-green-600" />
                    <span>Approved & Ready</span>
                  </span>
                  <span className="text-slate-600 font-bold text-[11px]">
                    {stats?.approved ?? 0}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick('Sent')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Sent' || currentTab === 'Unassigned' || currentTab === 'Waiting'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <UserX className="w-4 h-4 text-slate-500" />
                    <span>Sent Responses</span>
                  </span>
                  <span className="text-slate-500 font-semibold text-[11px]">
                    {stats?.sent ?? 0}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick('Rejected')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Rejected' || currentTab === 'Mentioned'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <AtSign className="w-4 h-4 text-red-500" />
                    <span>Rejected</span>
                  </span>
                  <span className="text-slate-500 font-semibold text-[11px]">
                    {stats?.rejected ?? 0}
                  </span>
                </button>

                <div className="pt-4 pb-1 px-3 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  Categories
                </div>

                <button
                  onClick={() => handleTabClick('Customer Inquiries')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Customer Inquiries'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4 text-blue-500" />
                    <span>Customer Inquiries</span>
                  </span>
                  <span className="text-slate-600 font-bold text-[11px]">
                    {catMap['customer_question'] ?? 0}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick('Shipping & Delivery')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Shipping & Delivery'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <Truck className="w-4 h-4 text-amber-500" />
                    <span>Shipping & Delivery</span>
                  </span>
                  <span className="text-slate-600 font-bold text-[11px]">
                    {catMap['shipping_question'] ?? 0}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick('Damaged / Defective')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Damaged / Defective'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-rose-500" />
                    <span>Damaged / Defective</span>
                  </span>
                  <span className="text-slate-600 font-bold text-[11px]">
                    {catMap['damaged_defective'] ?? 0}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick('Returns & Refunds')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Returns & Refunds'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <RotateCcw className="w-4 h-4 text-purple-500" />
                    <span>Returns & Refunds</span>
                  </span>
                  <span className="text-slate-600 font-bold text-[11px]">
                    {catMap['return_policy'] ?? 0}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick('Order Cancellations')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Order Cancellations'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span>Cancellations</span>
                  </span>
                  <span className="text-slate-600 font-bold text-[11px]">
                    {catMap['order_cancellation'] ?? 0}
                  </span>
                </button>

                <button
                  onClick={() => handleTabClick('Product Questions')}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                    currentTab === 'Product Questions'
                      ? 'bg-[#DBEAFE] text-[#1E40AF] font-semibold'
                      : 'text-slate-700 hover:bg-slate-200/60'
                  }`}
                >
                  <span className="flex items-center space-x-2">
                    <HelpIcon className="w-4 h-4 text-emerald-500" />
                    <span>Product Questions</span>
                  </span>
                  <span className="text-slate-600 font-bold text-[11px]">
                    {catMap['product_question'] ?? 0}
                  </span>
                </button>
              </div>
            </div>

            <div className="p-3 border-t border-slate-200/80 space-y-2">
              <div className="relative">
                <input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Search pinned filters and tags"
                  className="w-full bg-white border border-slate-200 rounded-md py-1.5 px-2.5 text-[11px] placeholder:text-slate-400 focus:outline-none focus:border-blue-500 shadow-sm"
                />
              </div>

              <div className="flex items-center justify-between px-1 text-slate-600 text-[12px] font-medium pt-1">
                <button className="flex items-center space-x-1 hover:text-slate-900">
                  <span>Tags</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <button className="p-1 hover:bg-slate-200 rounded text-slate-500">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="flex justify-end pt-1">
                <button
                  onClick={() => setSecondaryCollapsed(true)}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded"
                  title="Collapse sidebar"
                >
                  <PanelLeftClose className="w-4 h-4" />
                </button>
              </div>
            </div>
          </aside>
        )}

        {isMailboxPage && secondaryCollapsed && (
          <button
            onClick={() => setSecondaryCollapsed(false)}
            className="absolute left-[54px] top-4 z-40 bg-white border border-slate-200 p-1.5 rounded-r-md shadow-md text-slate-600 hover:text-slate-900"
            title="Expand sidebar"
          >
            <ChevronDown className="w-4 h-4 -rotate-90" />
          </button>
        )}

        <main className="flex-1 flex flex-col bg-white overflow-hidden">
          <header className="h-[52px] border-b border-slate-200 px-6 flex items-center justify-between shrink-0 bg-white">
            <div className="flex items-center space-x-3">
              {location.pathname.includes('/review/') && (
                <button
                  onClick={() => navigate('/emails')}
                  className="flex items-center space-x-1 text-slate-600 hover:text-slate-900 text-xs font-semibold px-2.5 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
                >
                  <ChevronDown className="w-4 h-4 rotate-90" />
                  <span>Back</span>
                </button>
              )}
              <div className="relative w-72">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                  className="w-full pl-9 pr-4 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-xs placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:bg-white transition-all shadow-inner"
                />
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleProcessEmails}
                disabled={isProcessing}
                className="bg-[#2563EB] hover:bg-blue-700 text-white font-semibold text-xs px-4 py-2 rounded-md shadow-sm transition-colors flex items-center space-x-1.5 disabled:opacity-50"
              >
                {isProcessing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                <span>New ticket</span>
              </button>
              <button className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-full" title="Help & Support">
                <HelpCircle className="w-5 h-5" />
              </button>
              <NotificationCenter />
            </div>
          </header>

          {!location.pathname.includes('/review/') && (
            <div className="px-6 py-3 flex items-center justify-between bg-white border-b border-slate-100 shrink-0">
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">{currentTab}</h1>
              <div className="flex items-center space-x-3 text-xs text-slate-600 font-medium">
                <button
                  type="button"
                  onClick={() => sort?.onSort(sort.field)}
                  disabled={!sort}
                  title={sort ? 'Reverse the order' : undefined}
                  className="flex items-center space-x-1 hover:text-slate-900 disabled:hover:text-slate-600"
                >
                  <ArrowUpDown className="w-3.5 h-3.5 text-slate-500" />
                  <span>
                    {sort
                      ? `Sort: ${SORT_LABELS[sort.field]} (${
                          sort.field === 'received_at'
                            ? sort.dir === 'desc' ? 'newest first' : 'oldest first'
                            : sort.dir === 'asc' ? 'A–Z' : 'Z–A'
                        })`
                      : 'Sort: None'}
                  </span>
                </button>
                <button className="p-1 hover:bg-slate-100 rounded text-slate-500">
                  <MoreVertical className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto relative">{children}</div>

          <footer className="h-10 border-t border-slate-200 px-6 flex items-center justify-end space-x-3 text-[11px] text-slate-500 bg-white shrink-0">
            {pagination ? (
              <>
                <span>
                  {totalCount === 0
                    ? 'showing 0 of 0'
                    : `showing ${(pagination.page - 1) * pagination.pageSize + 1} - ${Math.min(
                        pagination.page * pagination.pageSize,
                        totalCount
                      )} of ${totalCount}`}
                </span>
                <div className="flex items-center space-x-1">
                  <button
                    type="button"
                    onClick={() => pagination.onPageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    aria-label="Previous page"
                    className="p-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-1 font-medium text-slate-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => pagination.onPageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.totalPages}
                    aria-label="Next page"
                    className="p-1 rounded hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <span>showing 1 - {totalCount} of {totalCount}</span>
            )}
          </footer>
        </main>
      </div>

    </div>
  );
}
